import { AloftDate, expected } from '@aloft/toolkit-shared';
import { Modal } from '@aloft/toolkit-sui';

import _ from 'lodash';
import { useState } from 'react';

import { useAutofillProperty } from '../../../../hooks/useAutofillProperty.ts';
import { ToastOptions } from '../../../../hooks/useToaster/types.ts';
import { useToaster } from '../../../../hooks/useToaster/useToaster.tsx';
import { useZodForm } from '../../../../hooks/useZodForm.ts';
import { addComps } from '../../../../redux/features/actions/addComps.ts';
import {
  selectEffectiveDate,
  selectHasArvOption,
  selectOrder,
} from '../../../../redux/features/selectors/orderSelectors.ts';
import { selectImprovementsById } from '../../../../redux/features/selectors/propertyDataSelectors/improvementsSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks.ts';
import { useUpdateOrderFeatures } from '../../../orderManagement/components/OrderTable/hooks/useUpdateOrderFeatures.ts';
import { AddCompsForm } from './internal/AddCompsForm.tsx';
import { AddComparablesForm, schAddComparablesForm } from './types.ts';

export const AddComparablesModal = ({
  onModalClose,
  isModalOpen,
}: {
  onModalClose: () => void;
  isModalOpen: boolean;
}) => {
  const dispatch = useAppDispatch();
  const autofillProperty = useAutofillProperty();
  const updateOrderFeatures = useUpdateOrderFeatures();
  const effectiveDate = useAppSelector(selectEffectiveDate);
  const hasArvOption = useAppSelector(selectHasArvOption);
  const orderID = useAppSelector(selectOrder).id;
  const subjectQuality = useAppSelector((state) =>
    selectImprovementsById(state, 'subject'),
  )?.craftsmanQuality;

  const [isLoading, setIsLoading] = useState(false);
  const toast = useToaster();

  const autofillForm = useZodForm({
    schema: schAddComparablesForm,
    defaultValues: {
      // Without these empty strings, we get a warning that an
      // uncontrolled input is changing to a controlled input (even though
      // we do have defaults in the zod schema, which is a separate thing)
      address: {
        AddressLineText: '',
        unitNumber: '',
        CityName: '',
        StateCode: '',
        PostalCode: '',
      },
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const submitAutofill = autofillForm.handleSubmit(async (submitParams: AddComparablesForm) => {
    setIsLoading(true);
    const successToastOptions: ToastOptions = {
      type: 'success' as const,
      id: 'add-comps-via-import',
      title: 'Successfully added property!',
    };

    const handleCompletion = (toastOptions: ToastOptions) => {
      toast(toastOptions);
      setIsLoading(false);
      if (toastOptions.type === 'success') {
        onModalClose();
      }
    };

    try {
      const property = await autofillProperty({
        address: expected(submitParams.address),
        effectiveYear: AloftDate.schema.parse(expected(effectiveDate)).getYear(),
        propertyType: submitParams.propertyType,
        compType: submitParams.compType,
        orderID,
      });

      // Product ask: for manufactured and multifamily comps, override the quality
      // with what the subject's current quality is. SFRs will use the quality
      // determined through the "regular" code path.
      if (
        submitParams.propertyType === 'manufacturedHome' ||
        submitParams.propertyType === 'multiFamily'
      ) {
        const overrideQualityPreset =
          subjectQuality ?? property.editableData.improvements.craftsmanQuality;
        _.set(property, 'editableData.improvements.craftsmanQuality', overrideQualityPreset);
      }

      dispatch(addComps([property]));
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      updateOrderFeatures(orderID, [property]);
    } catch {
      handleCompletion({
        type: 'error',
        id: 'add-comps-via-import-error',
        title: 'Failed to add property.',
      });
      return undefined;
    }

    return handleCompletion(successToastOptions);
  });

  const autofillFields = autofillForm.watch();

  const isAutofillValid = () => {
    const { address, propertyType, compType } = autofillFields;

    return (
      address?.AddressLineText &&
      address?.CityName &&
      address?.StateCode &&
      address?.PostalCode &&
      propertyType &&
      (hasArvOption ? compType : true)
    );
  };

  return (
    <Modal
      size="lg"
      isOpen={isModalOpen}
      onClose={onModalClose}
      closeOnEsc
      title="Manually Add Comp"
      primaryAction={{
        id: 'add-btn',
        text: 'Save',
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onClick: submitAutofill,
        disabled: !isAutofillValid(),
        isLoading,
      }}
      secondaryAction={{
        id: 'cancel-btn',
        text: 'Cancel',
        onClick: () => {
          autofillForm.reset();
          onModalClose();
        },
      }}
    >
      <AddCompsForm form={autofillForm} />
      {autofillForm.formState.errors.address?.message && (
        <p>{autofillForm.formState.errors.address.message}</p>
      )}
    </Modal>
  );
};
